import React, { useEffect, useState } from 'react';
import { api, type CleaningNote } from '../lib/api';
import { useAuth } from '../lib/auth';
import { OperaWindow, OperaSection } from './OperaWindow';
import { fmtClock } from '../lib/format';

const MAX_NOTE_LENGTH = 2_000;

interface Props {
  roomId: string;
  roomNumber: string;
  roomDisplayName: string | null;
  onClose: () => void;
  /** Called after any change, so the caller can refresh its own counts. */
  onChanged?: () => void;
}

/**
 * Cleaning notes for one room: open ones first, resolved kept below as history.
 *
 * There is no edit path, matching ProfileNote. A note is an observation about
 * what a room was left like, and one that can be quietly rewritten afterwards
 * is worth less than one that cannot — the correction is another note.
 */
export function CleaningNotesModal({ roomId, roomNumber, roomDisplayName, onClose, onChanged }: Props) {
  const { user } = useAuth();
  const [notes, setNotes] = useState<CleaningNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canDelete = user?.role === 'MANAGER' || user?.role === 'ADMIN';

  async function load() {
    try {
      const r = await api.roomCleaningNotes(roomId);
      setNotes(r.notes);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [roomId]);

  async function add() {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    setError(null);
    try {
      await api.addCleaningNote(roomId, body);
      setDraft('');
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function resolve(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.resolveCleaningNote(id);
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    const reason = window.prompt('Reason for deleting this note (recorded):');
    if (reason === null) return;
    if (!reason.trim()) { setError('A reason is required to delete a note.'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.deleteCleaningNote(id, reason.trim());
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const open = notes.filter(n => !n.resolvedAt);
  const resolved = notes.filter(n => n.resolvedAt);
  const roomDesc = roomDisplayName ? `Room ${roomNumber} — ${roomDisplayName}` : `Room ${roomNumber}`;

  function renderNote(n: CleaningNote) {
    return (
      <div key={n.id} className={`cn-note ${n.resolvedAt ? 'resolved' : 'open'}`}>
        <div className="cn-note-body">{n.body}</div>
        <div className="cn-note-meta">
          {n.createdBy} · {fmtClock(n.createdAt)}
          {n.bookingProvider && (
            <span className="cn-note-booking"> · from {n.bookingProvider}&apos;s booking</span>
          )}
          {n.resolvedAt && (
            <span className="cn-note-resolved"> · done {fmtClock(n.resolvedAt)} by {n.resolvedBy}</span>
          )}
        </div>
        <div className="cn-note-actions">
          {!n.resolvedAt && (
            <button type="button" className="rt-btn" disabled={busy} onClick={() => resolve(n.id)}>
              Mark done
            </button>
          )}
          {canDelete && (
            <button type="button" className="rt-btn" disabled={busy} onClick={() => remove(n.id)}>
              Delete
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <OperaWindow
      title="CLEANING NOTES"
      subtitle={roomDesc}
      onClose={onClose}
      size="compact"
      sideButtons={[{ label: 'Close', onClick: onClose }]}
    >
      {error && <div className="modal-error">{error}</div>}

      <OperaSection title="Add a note">
        <textarea
          className="cn-draft"
          rows={3}
          maxLength={MAX_NOTE_LENGTH}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="e.g. client was drunk and vomited, needs extra attention"
          autoFocus
        />
        <div className="cn-draft-actions">
          <span className="cn-draft-count">{draft.length}/{MAX_NOTE_LENGTH}</span>
          <button type="button" className="rt-btn" disabled={busy || !draft.trim()} onClick={add}>
            {busy ? 'Saving…' : 'Add note'}
          </button>
        </div>
      </OperaSection>

      <OperaSection title={`Open (${open.length})`}>
        {loading && <div className="rt-empty">Loading…</div>}
        {!loading && open.length === 0 && <div className="rt-empty">Nothing outstanding for this room.</div>}
        {open.map(renderNote)}
      </OperaSection>

      {resolved.length > 0 && (
        <OperaSection title={`History (${resolved.length})`}>
          {resolved.map(renderNote)}
        </OperaSection>
      )}

      <div style={{ fontSize: 11, color: '#555', marginTop: 8 }}>
        Open notes close automatically when the room is next marked clean.
      </div>
    </OperaWindow>
  );
}
